import { type FC, useCallback, useState } from 'react'
import { Check, Copy, ExternalLink } from 'lucide-react'

import { Button } from '@modules/shadcn/ui/Button/Button'
import { Card, CardContent } from '@modules/shadcn/ui/Card/Card'
import { Label } from '@modules/shadcn/ui/Label/Label'
import { Textarea } from '@modules/shadcn/ui/Textarea/Textarea'

interface ReviewResultProps {
  companyName: string
  generatedText: string
  googlePlaceId: string
  onRestart: () => void
}

const buildGoogleReviewUrl = (placeId: string): string =>
  `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`

/** 生成された口コミ案の表示・編集 + Google マップ投稿動線（設計 §1） */
export const ReviewResult: FC<ReviewResultProps> = ({
  companyName,
  generatedText,
  googlePlaceId,
  onRestart,
}) => {
  const [text, setText] = useState(generatedText)
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // クリップボード API が使えない環境（権限拒否等）。本文は PII の可能性があるためログに出さない。
      console.error('[ReviewResult] クリップボードへのコピーに失敗しました')
    }
  }, [text])

  const handleOpenGoogle = useCallback(() => {
    if (!googlePlaceId) return
    window.open(buildGoogleReviewUrl(googlePlaceId), '_blank', 'noopener,noreferrer')
  }, [googlePlaceId])

  return (
    <div className="min-h-screen bg-muted">
      <header className="border-b bg-background px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-2xl">
          <h1 className="text-lg font-semibold text-foreground">口コミ案ができました</h1>
          <p className="mt-1 text-sm text-muted-foreground">{companyName}</p>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-4 px-4 py-6 sm:px-6">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm leading-relaxed text-foreground">
              アンケートのご回答、誠にありがとうございます。口コミの投稿完了まで、ご協力いただけますと幸いです。
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-6">
            <div className="space-y-2">
              <Label htmlFor="review-result-text" className="text-sm font-semibold">
                口コミ文（編集できます）
              </Label>
              <Textarea
                id="review-result-text"
                value={text}
                onChange={e => setText(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="button" variant="outline" className="flex-1" onClick={handleCopy}>
                {copied ? (
                  <>
                    <Check className="mr-1 size-4" />
                    コピーしました
                  </>
                ) : (
                  <>
                    <Copy className="mr-1 size-4" />
                    口コミをコピーする
                  </>
                )}
              </Button>
              {googlePlaceId && (
                <Button
                  type="button"
                  className="flex-1 bg-green-600 text-white hover:bg-green-700"
                  onClick={handleOpenGoogle}
                >
                  <ExternalLink className="mr-1 size-4" />
                  Googleマップに投稿する
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-2 p-4">
            <p className="text-xs leading-relaxed text-muted-foreground">
              ※
              投稿いただく口コミには、個人情報（氏名・住所・電話番号など）や事実と異なる内容を含めないようご注意ください。投稿内容はお客様ご自身の責任で公開されます。
            </p>
          </CardContent>
        </Card>

        <div className="text-center">
          <Button type="button" variant="link" onClick={onRestart}>
            最初からやり直す
          </Button>
        </div>
      </main>
    </div>
  )
}
